import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack, Typography, Tooltip, IconButton } from '@mui/material'
import { ResponsiveSideContent } from 'components-shared'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'

import { useNavigate } from 'router'
import { useUser } from 'store'
import Status from 'components/global/Status'
import CardPreview, { CardHeader, CardBody as UICardBody, CardBlockInfo } from 'packages/ui/CardPreview'
import { FIND_AP_PARENT } from '../../operations-gql/queries'
import {
  availableToTranslationKey,
  availableToColor,
  availableToBgColor,
  getDeviceStatus,
  formatBatteryTimeRemaining,
  isRackType,
  normalizeRackAvailableLevel
} from '../../helpers'
import { parseUptime } from './config-columns'
import ConnectedCamerasList from './ConnectedCamerasList'
import CCTVDiskGraph from './graphs/CCTVDiskGraph'
import { TOPOLOGY_BY_SITE } from '../Topology/operations-gql/queries'
import {
  getEquipmentTopologyStatus,
  TOPOLOGY_TOOLTIP_KEYS,
  useNavigateToEquipmentMap,
  useNavigateToEquipmentTopology
} from './use-actions'
import {
  SsidTableWrapper,
  SsidTableHeader,
  SsidTableRow,
  SsidNameCell,
  SsidFreqHeader,
  SsidFreqCell
} from '../Home/Card/styles'
import EquipmentThumbnails from './EquipmentThumbnails'

const FreqIcon = ({ value }) => {
  if (value === null) return null
  return (
    <i
      className={`fa-light fa-${value ? 'check' : 'xmark'}`}
      style={{ color: value ? '#92bc62' : '#f59e0a', fontSize: 13 }}
    />
  )
}

const SsidTable = ({ ssids }) => {
  const { t } = useTranslation()
  if (!ssids || ssids.length === 0) return null
  return (
    <SsidTableWrapper>
      <SsidTableHeader>
        <SsidNameCell sx={{ fontSize: 11, fontWeight: 500, color: '#7A8096' }}>
          {t('supervision.card.ssid_name', 'SSID')}
        </SsidNameCell>
        <SsidFreqHeader>2.4 GHz</SsidFreqHeader>
        <SsidFreqHeader>5 GHz</SsidFreqHeader>
      </SsidTableHeader>
      {ssids.map(ssid => {
        const name = typeof ssid === 'string' ? ssid : ssid.name
        const has24 = typeof ssid === 'string' ? null : ssid.has_2_4ghz
        const has5 = typeof ssid === 'string' ? null : ssid.has_5ghz
        return (
          <SsidTableRow key={name}>
            <SsidNameCell>{name}</SsidNameCell>
            <SsidFreqCell>
              <FreqIcon value={has24} />
            </SsidFreqCell>
            <SsidFreqCell>
              <FreqIcon value={has5} />
            </SsidFreqCell>
          </SsidTableRow>
        )
      })}
    </SsidTableWrapper>
  )
}

const BtnNavigateToMap = ({ equipment }) => {
  const { t } = useTranslation()
  const { navigateToMap, loading } = useNavigateToEquipmentMap(equipment)

  return (
    <Box>
      {loading ? (
        <Typography variant={'caption'}>{t('supervision.card.building_loading')}</Typography>
      ) : (
        <Tooltip title={t('supervision.card.tooltip_btn')} arrow>
          <IconButton
            onClick={navigateToMap}
            sx={{
              width: 32,
              height: 32,
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#F6F7F8'
              }
            }}>
            <Box component="i" className="fa-light fa-buildings" sx={{ fontSize: 14, color: '#7A8096' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

const BtnNavigateToTopology = ({ equipment }) => {
  const { t } = useTranslation()
  const { data, loading } = useQuery(TOPOLOGY_BY_SITE, {
    variables: { sf_id: equipment?.site?.sf_id },
    skip: !equipment?.site?.sf_id,
    fetchPolicy: 'cache-and-network'
  })
  const topology = data?.topology
  const topologyStatus = getEquipmentTopologyStatus({ loading, topology, mac: equipment?.mac })
  const disabled = topologyStatus !== 'available'
  const { navigateToTopology } = useNavigateToEquipmentTopology(equipment, topology)

  return (
    <Tooltip title={t(TOPOLOGY_TOOLTIP_KEYS[topologyStatus])} arrow>
      <Box>
        <IconButton
          onClick={navigateToTopology}
          disabled={disabled}
          sx={{
            width: 32,
            height: 32,
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#F6F7F8'
            },
            '&.Mui-disabled': {
              borderColor: '#E5E7EB',
              opacity: 0.55
            }
          }}>
          <Box component="i" className="fa-regular fa-circle-nodes" sx={{ fontSize: 14, color: '#7A8096' }} />
        </IconButton>
      </Box>
    </Tooltip>
  )
}

const Header = ({ equipment }) => {
  const { t } = useTranslation()
  const [user] = useUser()
  const status = useMemo(() => {
    const rawLevel = ['hibernation', 'works'].includes(equipment.available_level)
      ? `${equipment.available_level}_${equipment.is_reachable ? 'up' : 'down'}`
      : equipment.available_level
    const resolvedLevel = isRackType(equipment?.type) ? normalizeRackAvailableLevel(rawLevel) : rawLevel
    const label = t(availableToTranslationKey(resolvedLevel))
    return {
      label: equipment.manufacturer?.toLowerCase() === 'juniper' ? '-' : label,
      color: availableToColor(resolvedLevel),
      bgcolor: availableToBgColor(resolvedLevel)
    }
  }, [equipment, t])
  const showBtnMap = useMemo(() => {
    if (equipment.__typename) {
      return !['TV', 'Chromecast'].includes(equipment.__typename)
    }
    return !['tv', 'chromecast'].includes(equipment.type)
  }, [equipment])
  const showBtnTopology = useMemo(() => {
    return Boolean((user?.admin || user?.roles?.topology) && equipment?.site?.sf_id && equipment?.mac)
  }, [equipment, user])

  return (
    <CardHeader>
      <Stack direction="column" alignItems="center" gap={1.5}>
        {equipment.manufacturer?.toLowerCase() !== 'juniper' && (
          <Status status={status.label} color={status.color} bgcolor={status.bgcolor} isUppercase={false} />
        )}
        <Typography fontWeight={500} fontSize={18}>
          {equipment.label}
        </Typography>
        {(showBtnMap || showBtnTopology) && (
          <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
            {showBtnMap && <BtnNavigateToMap equipment={equipment} />}
            {showBtnTopology && <BtnNavigateToTopology equipment={equipment} />}
          </Stack>
        )}
      </Stack>
    </CardHeader>
  )
}

const Body = ({ equipment }) => {
  const { t } = useTranslation()
  const isAp = useMemo(() => {
    return equipment?.__typename === 'AP' || equipment?.type === 'ap'
  }, [equipment])
  const isFirewall = useMemo(() => {
    return equipment?.__typename === 'Firewall' || equipment?.type === 'firewall'
  }, [equipment])
  const isChromecast = useMemo(() => {
    return equipment?.__typename === 'Chromecast' || equipment?.type === 'chromecast'
  }, [equipment])
  const isCctvServer = useMemo(() => {
    return equipment?.__typename === 'CCTVServer' || equipment?.type === 'cctv_server'
  }, [equipment])
  const isCctvCamera = useMemo(() => {
    return equipment?.__typename === 'CCTVCamera' || equipment?.type === 'cctv_camera'
  }, [equipment])
  const isInverter = useMemo(() => {
    return equipment?.__typename === 'Inverter' || equipment?.type === 'inverter'
  }, [equipment])

  const subtitleUptime = useMemo(() => {
    if ([0, undefined].includes(equipment.system_uptime)) {
      return '-'
    }
    const { nbDay, hour } = parseUptime(equipment.system_uptime)
    return nbDay ? t('supervision.tables.system_uptime_value', { count: nbDay, hour }) : hour
  }, [equipment, t])

  const cloudStatus = useMemo(() => {
    if (!isCctvServer || !equipment.cloud_connection) return null

    const isConnected = equipment.cloud_connection.status
    return {
      label: isConnected ? t('supervision.cctv.connected') : t('supervision.cctv.disconnected'),
      color: isConnected ? availableToColor('up') : availableToColor('down'),
      bgcolor: isConnected ? availableToBgColor('up') : availableToBgColor('down')
    }
  }, [equipment, isCctvServer, t])

  const batteryTimeDisplay = useMemo(() => {
    if (!isInverter || equipment.battery_time_remaining == null) return '-'
    return formatBatteryTimeRemaining(equipment.battery_time_remaining)
  }, [equipment, isInverter])

  const { data: parentData } = useQuery(FIND_AP_PARENT, {
    variables: { mac: equipment.mac },
    skip: !isAp || !equipment.mac
  })

  const parent = parentData?.ap

  const parentDeviceStatus = useMemo(() => {
    if (!isAp || !parent?.parent_label) return null
    return getDeviceStatus({ available_level: parent.parent_status, is_reachable: parent.parent_is_reachable })
  }, [parent, isAp])

  return (
    <Box
      sx={{
        overflowY: 'auto',
        overflowX: 'hidden',
        maxHeight: isCctvServer ? 'calc(100vh - 420px)' : '100%',
        height: isCctvServer ? 'auto' : '100%',
        paddingRight: isCctvServer ? '8px' : '0',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }}>
      <UICardBody grid>
        <CardBlockInfo title={t('supervision.card.account')} subtitle={equipment.site?.account_parent?.name} />
        <CardBlockInfo title={t('supervision.card.site')} subtitle={equipment.site?.name} />
        <CardBlockInfo title={t('supervision.card.building')} subtitle={equipment.building_name || '-'} />
        <CardBlockInfo title={t('supervision.card.level')} subtitle={equipment.floor_name || '-'} />
        {isChromecast && <CardBlockInfo title={t('supervision.card.room')} subtitle={equipment.room || '-'} />}
        <CardBlockInfo title={t('supervision.card.manufacturer')} subtitle={equipment.manufacturer || '-'} />
        <CardBlockInfo title={t('supervision.card.model')} subtitle={equipment.model || '-'} />
        {isFirewall && (
          <>
            <CardBlockInfo title={t('supervision.popup.serial')} subtitle={equipment.serial || '-'} />
            {equipment.type && <CardBlockInfo title={t('supervision.card.type')} subtitle={equipment.type} />}
          </>
        )}
        {!isRackType(equipment?.type) && (
          <CardBlockInfo title={t('supervision.card.mac_address')} subtitle={equipment.mac || '-'} />
        )}
        {!isRackType(equipment?.type) && (
          <CardBlockInfo title={t('supervision.card.ip_address')} subtitle={equipment.ip_address || '-'} />
        )}
        {isFirewall && (
          <>
            <CardBlockInfo
              title={t('supervision.card.total_tunnels')}
              subtitle={[undefined, null].includes(equipment.total_tunnels) ? '-' : equipment.total_tunnels}
            />
            <CardBlockInfo
              title={t('supervision.card.tunnels_up')}
              subtitle={[undefined, null].includes(equipment.tunnels_up) ? '-' : equipment.tunnels_up}
            />
            <CardBlockInfo title={t('supervision.tables.system_uptime_col')} subtitle={subtitleUptime} />
          </>
        )}
        {isInverter && (
          <>
            <CardBlockInfo
              title={t('supervision.card.inverter.battery_percentage')}
              subtitle={equipment.battery_charge != null ? `${equipment.battery_charge} %` : '0 %'}
            />
            <CardBlockInfo
              title={t('supervision.card.inverter.battery_remaining_autonomy')}
              subtitle={batteryTimeDisplay}
            />
            <CardBlockInfo
              title={t('supervision.card.inverter.power_consumption')}
              subtitle={equipment.total_load != null ? `${equipment.total_load} W` : '0 W'}
            />
            {equipment.battery_status && (
              <CardBlockInfo title={t('supervision.card.battery_status')} subtitle={equipment.battery_status} />
            )}
            {equipment.battery_replace_date && (
              <CardBlockInfo
                title={t('supervision.card.battery_replace_date')}
                subtitle={equipment.battery_replace_date}
              />
            )}
          </>
        )}
        {isCctvServer && cloudStatus && (
          <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
            <Stack direction="row" alignItems="flex-start" gap={3}>
              {cloudStatus && (
                <Stack direction="column" gap={0.5}>
                  <Typography variant="h1Card">{t('supervision.cctv.cloud_status')}</Typography>
                  <Stack direction="row" gap={1} alignItems="center" mt={0.2}>
                    <Status
                      status={cloudStatus.label}
                      color={cloudStatus.color}
                      bgcolor={cloudStatus.bgcolor}
                      isUppercase={false}
                      fontSize={10}
                    />
                  </Stack>
                </Stack>
              )}
              {equipment.disk_usage != null && (
                <CardBlockInfo
                  title={t('supervision.cctv.total_disk_usage')}
                  subtitle={`${equipment.disk_usage.toFixed(2)} %`}
                />
              )}
            </Stack>
          </Box>
        )}
        {isCctvCamera && (
          <>
            <CardBlockInfo title={t('supervision.cctv.video_codec')} subtitle={equipment.video_codec || '-'} />
            <CardBlockInfo
              title={t('supervision.cctv.video_resolution')}
              subtitle={
                equipment.video_resolution
                  ? `${equipment.video_resolution.width} x ${equipment.video_resolution.height}`
                  : '-'
              }
            />
            <CardBlockInfo
              title={t('supervision.cctv.firmware_version')}
              subtitle={equipment.firmware_version || '-'}
            />
          </>
        )}
      </UICardBody>

      {isAp && (
        <Box display={'flex'} flexDirection={'column'} mt={1} gap={2}>
          <CardBlockInfo title={t('supervision.card.devices')} subtitle={equipment.connected_stations || '0'} />
          <CardBlockInfo
            title={t('supervision.card.frequency')}
            subtitle={
              <Stack>
                <Stack direction={'row'} gap={1} alignItems={'center'}>
                  <Typography fontSize={12} fontWeight={400} color={'#7A8096'}>
                    {t('supervision.card.24_ghz')}
                  </Typography>
                  <Box component="i" className="fa-light fa-arrow-right" />
                  <Typography fontSize={12} fontWeight={400}>
                    {equipment.channel_g ? `${t('supervision.card.canal')} ${equipment.channel_g}` : '-'}
                  </Typography>
                </Stack>
                <Stack direction={'row'} gap={1} alignItems={'center'}>
                  <Typography fontSize={12} fontWeight={400} color={'#7A8096'}>
                    {t('supervision.card.5_ghz')}
                  </Typography>
                  <Box component="i" className="fa-light fa-arrow-right" />
                  <Typography fontSize={12} fontWeight={400}>
                    {equipment.channel_a ? `${t('supervision.card.canal')} ${equipment.channel_a}` : '-'}
                  </Typography>
                </Stack>
              </Stack>
            }
          />
          {parent?.parent_label && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <CardBlockInfo title={t('supervision.card.parent')}>
                <Stack>
                  <Typography fontSize={13} fontWeight={400}>
                    {parent.parent_label}
                  </Typography>
                  {parent.parent_mac && (
                    <Typography fontSize={11} fontWeight={400} color={'#7A8096'} fontFamily="Consolas, monospace">
                      {parent.parent_mac}
                    </Typography>
                  )}
                </Stack>
              </CardBlockInfo>
              {parent.parent_port && (
                <CardBlockInfo title={t('supervision.card.parent_port')} subtitle={parent.parent_port} />
              )}
              {parentDeviceStatus?.level && (
                <CardBlockInfo title={t('supervision.card.status_parent')}>
                  <Box mt={0.5}>
                    <Status
                      status={t(availableToTranslationKey(parentDeviceStatus.level))}
                      color={parentDeviceStatus.color}
                      bgcolor={parentDeviceStatus.bgcolor}
                      isUppercase={false}
                      fontSize={10}
                      sx={{ alignSelf: 'flex-start' }}
                    />
                  </Box>
                </CardBlockInfo>
              )}
            </Box>
          )}
          <SsidTable ssids={equipment.ssids || equipment.ssid_names} />
        </Box>
      )}

      {isCctvServer && (
        <>
          <ConnectedCamerasList cameras={equipment.connected_cameras} />
          <CCTVDiskGraph device={equipment} />
          <Box sx={{ height: '24px' }} />
        </>
      )}
      <EquipmentThumbnails uuid={equipment?.uuid} />
    </Box>
  )
}

const Card = ({ equipment }) => {
  const { t } = useTranslation()
  const { hardwareType } = useParams()

  const navigate = useNavigate()
  const handleClose = () => navigate({ pathname: `/monitoring/infras/hardwares/${hardwareType}` })
  if (!equipment) {
    return null
  }
  return (
    <ResponsiveSideContent onClose={handleClose} style={{ marginTop: 0 }}>
      <CardPreview title={t('ui_elements.card_title')} onClose={handleClose}>
        <Header equipment={equipment} />
        <Body equipment={equipment} />
      </CardPreview>
    </ResponsiveSideContent>
  )
}

export default Card
